import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, CreditCard, ShieldCheck, ExternalLink, Unlink, Search, Copy, FileSearch, FileText, Save, Ticket as TicketIcon, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RFCField } from "@/components/ui/forms";

// Catálogos oficiales del SAT (CFDI 4.0)
const REGIMENES_SAT = [
  ["601", "General de Ley Personas Morales"],
  ["603", "Personas Morales con Fines no Lucrativos"],
  ["605", "Sueldos y Salarios e Ingresos Asimilados a Salarios"],
  ["606", "Arrendamiento"],
  ["607", "Régimen de Enajenación o Adquisición de Bienes"],
  ["608", "Demás ingresos"],
  ["610", "Residentes en el Extranjero sin Establecimiento Permanente"],
  ["611", "Ingresos por Dividendos (socios y accionistas)"],
  ["612", "Personas Físicas con Actividades Empresariales y Profesionales"],
  ["614", "Ingresos por intereses"],
  ["616", "Sin obligaciones fiscales"],
  ["620", "Sociedades Cooperativas de Producción"],
  ["621", "Incorporación Fiscal"],
  ["622", "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras"],
  ["625", "Régimen de Actividades Empresariales con ingresos vía Plataformas Tecnológicas"],
  ["626", "Régimen Simplificado de Confianza (RESICO)"],
];
const USO_CFDI = [
  ["G01", "Adquisición de mercancías"],
  ["G02", "Devoluciones, descuentos o bonificaciones"],
  ["G03", "Gastos en general"],
  ["I01", "Construcciones"],
  ["I02", "Mobiliario y equipo de oficina por inversiones"],
  ["I04", "Equipo de cómputo y accesorios"],
  ["I08", "Otra maquinaria y equipo"],
  ["CP01", "Pagos"],
  ["S01", "Sin efectos fiscales"],
];

const STATUS_MSG = {
  success: { type: "success", msg: "Cuenta de Mercado Pago vinculada correctamente." },
  cancelled: { type: "error", msg: "Vinculación cancelada. No se conectó ninguna cuenta." },
  error: { type: "error", msg: "No se pudo completar la vinculación con Mercado Pago." },
};

export default function ConfiguracionFinanciera() {
  const [empresa, setEmpresa] = useState(null);
  const [cuenta, setCuenta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [params, setParams] = useSearchParams();
  // WCS-43402 — Diagnóstico de soporte (persistente)
  const [diagType, setDiagType] = useState("folio");
  const [diagQuery, setDiagQuery] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  // Datos fiscales (CFDI)
  const [fiscal, setFiscal] = useState({ rfc: "", razon_social: "", codigo_postal: "", regimen_fiscal: "", uso_cfdi_default: "", email_facturacion: "" });
  const [savingFiscal, setSavingFiscal] = useState(false);
  // Sprint 26.0 — Motivos de cortesía configurables por tenant
  const [motivos, setMotivos] = useState([]);
  const [nuevoMotivo, setNuevoMotivo] = useState("");
  const [savingMotivos, setSavingMotivos] = useState(false);

  const cargarMotivos = useCallback(async () => {
    try { const { data } = await api.get("/operativa/motivos-cortesia"); setMotivos(data?.motivos || []); } catch { /* noop */ }
  }, []);

  const guardarMotivos = async (lista) => {
    setSavingMotivos(true);
    try {
      const { data } = await api.put("/operativa/motivos-cortesia", { motivos: lista });
      setMotivos(data?.motivos || []);
      toast.success("Motivos de cortesía actualizados.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingMotivos(false); }
  };

  const guardarFiscal = async () => {
    setSavingFiscal(true);
    try {
      const { data } = await api.put("/mi-empresa/fiscal", fiscal);
      setEmpresa(data);
      toast.success("Datos fiscales actualizados.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingFiscal(false); }
  };

  const buscarDiag = async () => {
    const q = diagQuery.trim();
    if (!q) { toast.error("Escribe un folio ParkNova, order_id o id_transacción."); return; }
    setDiagLoading(true); setDiagResult(null);
    try {
      const { data } = await api.get(`/mercadopago/point/diagnostico?${diagType}=${encodeURIComponent(q)}`);
      setDiagResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setDiagLoading(false); }
  };

  const copiarDiag = async () => {
    if (!diagResult) return;
    const d = diagResult; const g = d.diagnostico || {};
    const txt = [
      "=== DIAGNÓSTICO WCS-43402 — Mercado Pago Point ===",
      `id_transaccion: ${d.id_transaccion}`,
      `Folio ParkNova: ${d.folio_parknova || "—"}`,
      `order_id: ${d.order_id || "—"}`,
      `Estado ParkNova: ${d.estado_transaccion}`,
      `Monto base: $${d.monto_base}  |  Comisión: $${d.comision_aplicada}  |  Total: $${d.total_cobrado}`,
      `Folio de autorización: ${d.folio_autorizacion || "—"}`,
      "",
      "--- 1. REQUEST REAL DE CREACIÓN (Authorization redactado) ---",
      JSON.stringify(g.request, null, 2),
      "",
      "--- 2. RESPONSE REAL DE CREACIÓN ---",
      JSON.stringify(g.response, null, 2),
      g.exception ? "\n--- EXCEPCIÓN DE RED ---\n" + JSON.stringify(g.exception, null, 2) : "",
      "",
      "--- 3. LÍNEA DE TIEMPO DE ESTADOS ---",
      JSON.stringify(g.timeline, null, 2),
      "",
      "--- 4. CANCELACIÓN ---",
      JSON.stringify(g.cancelacion, null, 2),
      "",
      "--- 5. TERMINAL ---",
      JSON.stringify(g.terminal, null, 2),
      "",
      "--- 6. CUENTA ---",
      JSON.stringify(g.account, null, 2),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Diagnóstico copiado para soporte.");
    } catch {
      toast.error("No se pudo copiar automáticamente. Selecciona el texto manualmente.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mi-empresa");
      setEmpresa(data);
      setFiscal({
        rfc: data.rfc || "",
        razon_social: data.razon_social || "",
        codigo_postal: data.codigo_postal || "",
        regimen_fiscal: data.regimen_fiscal || "",
        uso_cfdi_default: data.uso_cfdi_default || "",
        email_facturacion: data.email_facturacion || "",
      });
      if (data?.mp_conectado) {
        try { const { data: c } = await api.get("/mercadopago/cuenta"); setCuenta(c); }
        catch { setCuenta(null); }
      } else {
        setCuenta(null);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { cargarMotivos(); }, [cargarMotivos]);

  useEffect(() => {
    const st = params.get("mp_status");
    if (st && STATUS_MSG[st]) {
      const { type, msg } = STATUS_MSG[st];
      type === "success" ? toast.success(msg) : toast.error(msg);
      params.delete("mp_status");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const conectar = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/mercadopago/auth-url");
      if (data?.url) {
        // Redirige en la ventana principal para evadir X-Frame-Options del iframe de preview
        try { window.top.location.href = data.url; }
        catch { window.location.href = data.url; }
      }
      else { toast.error("No se pudo generar el enlace de autorización"); setConnecting(false); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const conectado = !!empresa?.mp_conectado;

  const desconectar = async () => {
    setDisconnecting(true);
    try {
      await api.post("/mercadopago/disconnect");
      toast.success("Cuenta de Mercado Pago desconectada.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  const cuentaLabel = cuenta?.email || cuenta?.nickname || (cuenta?.user_id ? `ID ${cuenta.user_id}` : null);

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="config-financiera-page">
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Configuración</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Configuración Financiera</h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-xl">Administra tus datos fiscales para facturación (CFDI) y la vinculación de tu pasarela de pagos.</p>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <Tabs defaultValue="fiscal" className="max-w-3xl" data-testid="config-tabs">
            <TabsList className="mb-6">
              <TabsTrigger value="fiscal" data-testid="tab-fiscal">Datos Fiscales</TabsTrigger>
              <TabsTrigger value="pagos" data-testid="tab-pagos">Pasarela de pagos</TabsTrigger>
              <TabsTrigger value="cortesias" data-testid="tab-cortesias">Cortesías</TabsTrigger>
            </TabsList>

            <TabsContent value="fiscal">
              <div className="border border-[#e4e4e7] bg-white max-w-xl" data-testid="fiscal-card">
                <div className="flex items-center gap-3 px-6 py-5 border-b border-[#e4e4e7]">
                  <div className="h-10 w-10 bg-[#0A0A0A] flex items-center justify-center"><FileText className="h-5 w-5 text-[#FFCC00]" strokeWidth={2.2} /></div>
                  <div>
                    <p className="font-display font-black text-lg leading-none tracking-tight">Datos Fiscales (CFDI)</p>
                    <p className="text-[10px] tracking-[0.2em] text-[#52525B] font-bold mt-1 uppercase">Se usan al emitir tus facturas</p>
                  </div>
                </div>
                <div className="px-6 py-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">RFC</Label>
                    <RFCField value={fiscal.rfc} onChange={(v) => setFiscal({ ...fiscal, rfc: v })} placeholder="XAXX010101000" data-testid="fiscal-rfc" className="rounded-none border-[#0A0A0A]" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Razón Social</Label>
                    <Input value={fiscal.razon_social} onChange={(e) => setFiscal({ ...fiscal, razon_social: e.target.value })} placeholder="Nombre o razón social" data-testid="fiscal-razon" className="rounded-none border-[#0A0A0A]" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Código Postal</Label>
                    <Input value={fiscal.codigo_postal} onChange={(e) => setFiscal({ ...fiscal, codigo_postal: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="06000" data-testid="fiscal-cp" className="rounded-none border-[#0A0A0A] font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Régimen Fiscal</Label>
                    <Select value={fiscal.regimen_fiscal} onValueChange={(v) => setFiscal({ ...fiscal, regimen_fiscal: v })}>
                      <SelectTrigger className="rounded-none border-[#0A0A0A]" data-testid="fiscal-regimen"><SelectValue placeholder="Selecciona un régimen" /></SelectTrigger>
                      <SelectContent>{REGIMENES_SAT.map(([c, n]) => <SelectItem key={c} value={c}>{c} — {n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Uso de CFDI</Label>
                    <Select value={fiscal.uso_cfdi_default} onValueChange={(v) => setFiscal({ ...fiscal, uso_cfdi_default: v })}>
                      <SelectTrigger className="rounded-none border-[#0A0A0A]" data-testid="fiscal-uso"><SelectValue placeholder="Selecciona un uso de CFDI" /></SelectTrigger>
                      <SelectContent>{USO_CFDI.map(([c, n]) => <SelectItem key={c} value={c}>{c} — {n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Correo para recepción de facturas</Label>
                    <Input type="email" value={fiscal.email_facturacion} onChange={(e) => setFiscal({ ...fiscal, email_facturacion: e.target.value })} placeholder="facturas@tuempresa.com" data-testid="fiscal-email" className="rounded-none border-[#0A0A0A]" />
                    <p className="text-xs text-[#52525B]">A este correo se enviará tu factura ya timbrada. Es obligatorio para solicitar CFDI.</p>
                  </div>
                  <button onClick={guardarFiscal} disabled={savingFiscal} data-testid="fiscal-save" className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
                    {savingFiscal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar datos fiscales
                  </button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pagos">
            <div>
            {/* Tarjeta Dark Mode Mercado Pago */}
            <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] overflow-hidden max-w-xl" data-testid="mp-card">
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 bg-[#009EE3] flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-white" strokeWidth={2.2} />
                  </div>
                  <div>
                    <p className="font-display font-black text-lg leading-none tracking-tight">Mercado Pago</p>
                    <p className="text-[10px] tracking-[0.25em] text-white/50 font-bold mt-1 uppercase">OAuth 2.0 · Connect</p>
                  </div>
                </div>
                {conectado && (
                  <span data-testid="mp-status-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#10B981] text-white text-[10px] font-bold uppercase tracking-wider">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Activa
                  </span>
                )}
              </div>

              <div className="px-6 py-8">
                {conectado ? (
                  <div className="flex flex-col items-center text-center gap-4" data-testid="mp-connected">
                    <div className="h-16 w-16 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center">
                      <ShieldCheck className="h-8 w-8 text-[#34D399]" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="font-display text-2xl font-black tracking-tight text-[#34D399]">Pasarela de Pagos Activa</p>
                      <p className="text-sm text-white/60 mt-1">Los cobros con tarjeta se procesarán vía Mercado Pago.</p>
                    </div>
                    {cuentaLabel && (
                      <p className="text-sm text-white/70" data-testid="mp-cuenta-label">
                        Cuenta vinculada: <span className="font-mono text-white font-semibold">{cuentaLabel}</span>
                      </p>
                    )}
                    <button
                      onClick={desconectar}
                      disabled={disconnecting}
                      data-testid="mp-disconnect-btn"
                      className="mt-1 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/50 hover:text-[#F87171] transition-colors duration-200 disabled:opacity-60"
                    >
                      {disconnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlink className="h-3.5 w-3.5" />}
                      {disconnecting ? "Desconectando…" : "Desconectar cuenta"}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-center gap-5" data-testid="mp-disconnected">
                    <p className="text-sm text-white/70 max-w-sm">Aún no has vinculado tu cuenta. Conéctala para habilitar los cobros con tarjeta en tus cajas.</p>
                    <button
                      onClick={conectar}
                      disabled={connecting}
                      data-testid="mp-connect-btn"
                      className="btn-hard h-12 px-6 bg-[#009EE3] hover:bg-[#0088c7] text-white border border-[#009EE3] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60"
                    >
                      {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                      {connecting ? "Redirigiendo…" : "Conectar con Mercado Pago"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* WCS-43402 — Diagnóstico de soporte (privado, TenantAdmin) */}
            <div className="mt-8 border border-[#E4E4E7] bg-white" data-testid="mp-diagnostico-section">
              <div className="flex items-center gap-3 px-6 py-5 border-b border-[#E4E4E7]">
                <div className="h-10 w-10 bg-[#0A0A0A] flex items-center justify-center">
                  <FileSearch className="h-5 w-5 text-[#FFCC00]" strokeWidth={2.2} />
                </div>
                <div>
                  <p className="font-display font-black text-lg leading-none tracking-tight">Diagnóstico de soporte</p>
                  <p className="text-[10px] tracking-[0.2em] text-[#52525B] font-bold mt-1 uppercase">Mercado Pago · Point · WCS-43402</p>
                </div>
              </div>
              <div className="px-6 py-6 space-y-4">
                <p className="text-sm text-[#52525B]">Busca el diagnóstico técnico persistido de un cobro con terminal para enviarlo a soporte. Nunca se muestran credenciales.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={diagType}
                    onChange={(e) => setDiagType(e.target.value)}
                    data-testid="diag-type-select"
                    className="h-11 border border-[#0A0A0A] bg-white px-3 text-sm font-semibold focus:outline-none"
                  >
                    <option value="folio">Folio ParkNova</option>
                    <option value="order_id">order_id (MP)</option>
                    <option value="id_transaccion">id_transacción</option>
                  </select>
                  <input
                    value={diagQuery}
                    onChange={(e) => setDiagQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") buscarDiag(); }}
                    placeholder="Ej. T-260723-9680"
                    data-testid="diag-query-input"
                    className="flex-1 h-11 border border-[#0A0A0A] bg-white px-3 text-sm font-mono focus:outline-none"
                  />
                  <button
                    onClick={buscarDiag}
                    disabled={diagLoading}
                    data-testid="diag-search-btn"
                    className="h-11 px-5 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 hover:bg-[#27272A] transition-colors duration-200 disabled:opacity-60"
                  >
                    {diagLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    Buscar
                  </button>
                </div>

                {diagResult && (
                  <div className="border border-[#E4E4E7] bg-[#FAFAFA]" data-testid="diag-result">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#E4E4E7]">
                      <div className="text-xs font-mono text-[#52525B] space-y-0.5">
                        <div>Folio: <span className="text-[#0A0A0A] font-semibold" data-testid="diag-folio">{diagResult.folio_parknova || "—"}</span></div>
                        <div>order_id: <span className="text-[#0A0A0A] font-semibold" data-testid="diag-order-id">{diagResult.order_id || "—"}</span></div>
                        <div>Estado ParkNova: <span className="text-[#0A0A0A] font-semibold" data-testid="diag-estado">{diagResult.estado_transaccion}</span></div>
                        <div>Base ${diagResult.monto_base} · Comisión ${diagResult.comision_aplicada} · Total ${diagResult.total_cobrado}</div>
                      </div>
                      <button
                        onClick={copiarDiag}
                        data-testid="diag-copy-btn"
                        className="h-10 px-4 bg-[#FFCC00] text-[#0A0A0A] font-black uppercase tracking-wider text-xs flex items-center gap-2 hover:bg-[#e6b800] transition-colors duration-200"
                      >
                        <Copy className="h-3.5 w-3.5" /> Copiar diagnóstico para soporte
                      </button>
                    </div>
                    <div className="px-4 py-3">
                      {diagResult.diagnostico ? (
                        <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap break-all bg-[#0A0A0A] text-[#D4D4D8] p-4 text-[11px] font-mono" data-testid="diag-json">
{JSON.stringify(diagResult.diagnostico, null, 2)}
                        </pre>
                      ) : (
                        <p className="text-sm text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] px-3 py-2" data-testid="diag-empty">
                          Esta transacción no tiene diagnóstico técnico persistido (probablemente se creó antes de habilitar la captura). Los cobros nuevos sí lo registrarán automáticamente.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
            </TabsContent>

            <TabsContent value="cortesias">
              <div className="border border-[#e4e4e7] bg-white max-w-xl" data-testid="cortesias-card">
                <div className="flex items-center gap-3 px-6 py-5 border-b border-[#e4e4e7]">
                  <div className="h-10 w-10 bg-[#0A0A0A] flex items-center justify-center"><TicketIcon className="h-5 w-5 text-[#A78BFA]" strokeWidth={2.2} /></div>
                  <div>
                    <p className="font-display font-black text-lg leading-none tracking-tight">Motivos de Cortesía</p>
                    <p className="text-[10px] tracking-[0.2em] text-[#52525B] font-bold mt-1 uppercase">Se eligen al aplicar un cobro $0.00</p>
                  </div>
                </div>
                <div className="px-6 py-6 space-y-4">
                  <p className="text-sm text-[#52525B]">Estos motivos aparecen en la caseta y en el Valet al aplicar una cortesía. Quedan registrados junto con el usuario que autoriza.</p>
                  <div className="space-y-2" data-testid="motivos-list">
                    {motivos.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 border border-[#e4e4e7] px-3 h-11" data-testid={`motivo-row-${i}`}>
                        <span className="flex-1 text-sm font-medium">{m}</span>
                        <button onClick={() => guardarMotivos(motivos.filter((_, idx) => idx !== i))} disabled={savingMotivos}
                          data-testid={`motivo-eliminar-${i}`} className="text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 flex items-center justify-center disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    {motivos.length === 0 && <p className="text-sm text-[#71717A] italic">No hay motivos configurados.</p>}
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-[#e4e4e7]">
                    <Input value={nuevoMotivo} onChange={(e) => setNuevoMotivo(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && nuevoMotivo.trim()) { guardarMotivos([...motivos, nuevoMotivo.trim()]); setNuevoMotivo(""); } }}
                      placeholder="Nuevo motivo (ej. VIP, Convenio)" data-testid="motivo-nuevo-input" className="h-11 rounded-none border-[#0A0A0A]" />
                    <button onClick={() => { if (nuevoMotivo.trim()) { guardarMotivos([...motivos, nuevoMotivo.trim()]); setNuevoMotivo(""); } }}
                      disabled={savingMotivos || !nuevoMotivo.trim()} data-testid="motivo-agregar-btn"
                      className="btn-hard h-11 px-5 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50">
                      {savingMotivos ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Agregar
                    </button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </motion.div>
    </DashboardLayout>
  );
}
